import React, { useState } from "react";
import { Hash, Calculator, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

// Chaldean/Pythagorean reduction
const reduceNum = (n) => {
  while (n > 9 && ![11, 22, 33].includes(n)) {
    n = String(n).split("").reduce((a, b) => a + parseInt(b, 10), 0);
  }
  return n;
};

const NUMBERS = [
  { n: 1, planet: "Sun ☉", key: "Leader", traits: "Ambitious, original, self-reliant. Born to lead but must guard against ego.", lucky: "Sunday · Gold · Red" },
  { n: 2, planet: "Moon ☾", key: "Diplomat", traits: "Sensitive, intuitive, peacemaker. Great advisors, need emotional grounding.", lucky: "Monday · Silver · Cream" },
  { n: 3, planet: "Jupiter ♃", key: "Optimist", traits: "Expressive, philosophical, lucky. Teachers, artists, spiritual seekers.", lucky: "Thursday · Yellow · Gold" },
  { n: 4, planet: "Rahu ☊", key: "Builder", traits: "Practical, disciplined, unconventional. Struggle & success go hand-in-hand.", lucky: "Sunday · Grey · Electric Blue" },
  { n: 5, planet: "Mercury ☿", key: "Communicator", traits: "Quick-witted, versatile, restless. Sales, media, travel roles thrive.", lucky: "Wednesday · Green · Silver" },
  { n: 6, planet: "Venus ♀", key: "Nurturer", traits: "Loving, artistic, family-oriented. Beauty, love & harmony are your themes.", lucky: "Friday · White · Pink" },
  { n: 7, planet: "Ketu ☋", key: "Mystic", traits: "Analytical, spiritual, introspective. Researchers, monks, deep thinkers.", lucky: "Monday · Purple · Sea Green" },
  { n: 8, planet: "Saturn ♄", key: "Executive", traits: "Ambitious, patient, karma-heavy. Slow success, high responsibility.", lucky: "Saturday · Black · Dark Blue" },
  { n: 9, planet: "Mars ♂", key: "Warrior", traits: "Courageous, fiery, protective. Excel in defence, sports & leadership.", lucky: "Tuesday · Red · Maroon" },
];

export default function NumerologyService() {
  const [dob, setDob] = useState("");
  const [name, setName] = useState("");
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!dob) return;
    const digits = dob.replace(/[^0-9]/g, "");
    const day = parseInt(dob.split("-")[2] || "0", 10);
    const birthNum = reduceNum(day);
    const lifePath = reduceNum(digits.split("").reduce((a, b) => a + parseInt(b, 10), 0));
    const nameNum = name
      ? reduceNum(name.toUpperCase().split("").reduce((a, c) => {
          const code = c.charCodeAt(0);
          return code >= 65 && code <= 90 ? a + ((code - 64 - 1) % 9) + 1 : a;
        }, 0))
      : null;
    setResult({ birthNum, lifePath, nameNum });
  };

  const info = (n) => NUMBERS.find((x) => x.n === n) || NUMBERS[0];

  return (
    <div className="relative min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04]">
          <div className="font-serif text-[40vw] text-[#D4AF37] leading-none select-none">7</div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20">
          <div className="text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-4">Ank Vidya · अंक विद्या</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-3xl">
            The <span className="italic gold-gradient">language of numbers.</span>
          </h1>
          <p className="mt-6 max-w-xl text-[#A0A4AB]">
            Every number carries a vibration. Discover yours — birth number, life-path & name resonance.
          </p>
        </div>
      </div>

      {/* Calculator */}
      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <div className="p-8 rounded-2xl bg-[#0A0C10] border border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <Calculator className="text-[#D4AF37]" size={22}/>
              <div className="font-serif text-2xl">Numerology Calculator</div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Full Name (optional)</label>
                <input
                  data-testid="num-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"
                  placeholder="e.g. Arjun Verma"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Date of Birth</label>
                <input
                  data-testid="num-dob-input"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"
                />
              </div>
              <button
                data-testid="num-calc-btn"
                onClick={calculate}
                className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition"
              >
                Reveal My Numbers
              </button>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          {!result ? (
            <div className="h-full min-h-[300px] rounded-2xl border border-dashed border-white/10 flex flex-col items-center justify-center p-8 text-center">
              <div className="font-serif text-6xl gold-gradient opacity-40">1  2  3</div>
              <div className="text-[#A0A4AB] mt-6">Enter your birth date to reveal your soul's numbers.</div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="num-result">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Birth Number", val: result.birthNum },
                  { label: "Life Path", val: result.lifePath },
                  { label: "Name Number", val: result.nameNum || "—" },
                ].map((c) => (
                  <div key={c.label} className="p-6 rounded-xl bg-gradient-to-br from-[#12151C] to-[#0A0C10] border border-[#D4AF37]/30 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-[#A0A4AB]">{c.label}</div>
                    <div className="font-serif text-6xl gold-gradient mt-2">{c.val}</div>
                  </div>
                ))}
              </div>

              {[result.birthNum, result.lifePath].map((n, i) => {
                const d = info(n);
                return (
                  <div key={i} className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center font-serif text-2xl text-[#D4AF37]">
                          {n}
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-[#A0A4AB]">
                            {i === 0 ? "Birth Number" : "Life-Path Number"}
                          </div>
                          <div className="font-serif text-xl">{d.key} · <span className="text-[#D4AF37]">{d.planet}</span></div>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-[#A0A4AB] mt-4 leading-relaxed">{d.traits}</p>
                    <div className="text-xs text-[#D4AF37] mt-3">Lucky: {d.lucky}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* All numbers grid */}
      <div className="border-t border-white/10 bg-[#0A0C10]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-8">
            The <span className="italic gold-gradient">nine planetary numbers.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-3">
            {NUMBERS.map((n) => (
              <div key={n.n} className="p-6 rounded-xl bg-[#050507] border border-white/10 hover:border-[#D4AF37]/40 transition">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-serif text-4xl gold-gradient">{n.n}</div>
                  <div className="text-xs text-[#D4AF37]">{n.planet}</div>
                </div>
                <div className="font-serif text-lg">{n.key}</div>
                <p className="text-xs text-[#A0A4AB] mt-2 leading-relaxed">{n.traits}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/astrologers" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] transition">
              Book a Numerologist <ArrowUpRight size={14}/>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
